import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Copy, Check, Download, QrCode, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useGetQuizQrQuery } from '../../api/apiSlice';

// Модалка «Поделиться»: ссылка + QR. QR приходит готовой data-URL'кой
// с бэка (генерим qrcode-пакетом, без клиентских зависимостей).
const ShareModal = ({ quizId, quizTitle, onClose }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetQuizQrQuery(quizId, { skip: !quizId });
  const [copied, setCopied] = useState(false);

  // Esc для закрытия — без хитростей.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const url = data?.url || '';
  const qrDataUrl = data?.qrDataUrl || '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('quiz.share.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('quiz.share.copyFailed'));
    }
  };

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `quiz-${quizId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('quiz.share.title')}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate break-words">
              {quizTitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={t('quiz.share.close')}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {isError ? (
            <p className="text-sm text-red-600 dark:text-red-400">{t('quiz.share.qrFailed')}</p>
          ) : (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-5 flex flex-col items-center">
              <div className="w-48 h-48 bg-white rounded-xl flex items-center justify-center overflow-hidden">
                {isLoading || !qrDataUrl ? (
                  <QrCode className="w-12 h-12 text-gray-300 animate-pulse" />
                ) : (
                  <img src={qrDataUrl} alt="QR" className="w-48 h-48 object-contain" />
                )}
              </div>
              <button
                type="button"
                onClick={downloadQr}
                disabled={!qrDataUrl}
                className="mt-4 inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> {t('quiz.share.downloadPng')}
              </button>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
              {t('quiz.share.linkLabel')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={url}
                readOnly
                onFocus={(e) => e.target.select()}
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none break-all"
              />
              <button
                type="button"
                onClick={copyLink}
                disabled={!url}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-sm font-medium disabled:opacity-50"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? t('quiz.share.ok') : t('quiz.share.copy')}
              </button>
            </div>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                <ExternalLink className="w-3 h-3" /> {t('quiz.share.openInNewTab')}
              </a>
            )}
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('quiz.share.publicHint')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
